/**
 * Feedback Modal
 * Form for submitting bug reports, feature requests, and general feedback
 */

import { useState, type FormEvent } from "react";
import { useMutation } from "@tanstack/react-query";
import { Modal } from "@components/admin/Modal";
import { Button } from "@components/common/Button";
import { toast } from "@stores/uiStore";
import { apiPost } from "@services/api";
import type {
  FeedbackType,
  FeedbackCreateRequest,
  FeedbackResponse,
} from "@/types/feedback";
import { cn } from "@utils/cn";

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const FEEDBACK_TYPES: {
  value: FeedbackType;
  label: string;
  icon: JSX.Element;
  description: string;
}[] = [
  {
    value: "bug",
    label: "Bug Report",
    icon: (
      <svg
        className="w-6 h-6"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
        />
      </svg>
    ),
    description: "Something isn't working correctly",
  },
  {
    value: "feature",
    label: "Feature Request",
    icon: (
      <svg
        className="w-6 h-6"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
        />
      </svg>
    ),
    description: "Suggest a new feature or improvement",
  },
  {
    value: "general",
    label: "General Feedback",
    icon: (
      <svg
        className="w-6 h-6"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
        />
      </svg>
    ),
    description: "Share your thoughts or questions",
  },
];

export function FeedbackModal({ isOpen, onClose }: FeedbackModalProps) {
  const [feedbackType, setFeedbackType] = useState<FeedbackType | null>(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");

  const submitMutation = useMutation({
    mutationFn: async (data: FeedbackCreateRequest) => {
      return apiPost<FeedbackResponse>("/feedback", data);
    },
    onSuccess: () => {
      toast.success(
        "Feedback Submitted",
        "Thank you! We appreciate your feedback.",
      );
      handleClose();
    },
    onError: () => {
      toast.error("Submission Failed", "Please try again later.");
    },
  });

  const handleClose = () => {
    setFeedbackType(null);
    setTitle("");
    setDescription("");
    onClose();
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!feedbackType || !title.trim() || !description.trim()) return;

    submitMutation.mutate({
      feedback_type: feedbackType,
      title: title.trim(),
      description: description.trim(),
      page_url: window.location.href,
    });
  };

  const isValid =
    feedbackType && title.trim().length >= 5 && description.trim().length >= 20;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Send Feedback"
      description="Help us improve ADK Platform"
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Step 1: Select Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            What type of feedback?
          </label>
          <div className="grid grid-cols-3 gap-3">
            {FEEDBACK_TYPES.map((type) => (
              <button
                key={type.value}
                type="button"
                onClick={() => setFeedbackType(type.value)}
                className={cn(
                  "flex flex-col items-center p-4 rounded-lg border-2 transition-all",
                  feedbackType === type.value
                    ? "border-primary-500 bg-primary-50 dark:bg-primary-900/20"
                    : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600",
                )}
              >
                <div
                  className={cn(
                    "mb-2",
                    feedbackType === type.value
                      ? "text-primary-500"
                      : "text-gray-400",
                  )}
                >
                  {type.icon}
                </div>
                <span
                  className={cn(
                    "text-sm font-medium",
                    feedbackType === type.value
                      ? "text-primary-700 dark:text-primary-300"
                      : "text-gray-700 dark:text-gray-300",
                  )}
                >
                  {type.label}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Step 2: Title */}
        <div>
          <label
            htmlFor="feedback-title"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
          >
            Title
          </label>
          <input
            id="feedback-title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Brief summary of your feedback"
            maxLength={255}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
          />
          <p className="mt-1 text-xs text-gray-500">
            {title.length}/255 characters (min 5)
          </p>
        </div>

        {/* Step 3: Description */}
        <div>
          <label
            htmlFor="feedback-description"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
          >
            Description
          </label>
          <textarea
            id="feedback-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={
              feedbackType === "bug"
                ? "Please describe what happened, what you expected, and steps to reproduce..."
                : feedbackType === "feature"
                  ? "Describe the feature you'd like and how it would help you..."
                  : "Share your thoughts, questions, or suggestions..."
            }
            rows={5}
            maxLength={5000}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white resize-none"
          />
          <p className="mt-1 text-xs text-gray-500">
            {description.length}/5000 characters (min 20)
          </p>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
          <Button type="button" variant="ghost" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={!isValid}
            isLoading={submitMutation.isPending}
          >
            Submit Feedback
          </Button>
        </div>
      </form>
    </Modal>
  );
}
